import React, { useEffect, useRef, useState } from "react";
import { Dropdown, IItems } from "@src/components/dropdown/Dropdown";
import { DateInput } from "@src/components/input/DateInput";
import { TimeInput } from "@src/components/input/TimeInput";
import { Input } from "@src/components/input/Input";
import { SelectYup } from "@src/components/select/SelectYup";
import { Control, UseFormReset, UseFormSetValue, useWatch } from "react-hook-form";
import { RangeSlider } from "@src/components/input/RangeSlider";
import { EventListHeaderFormProps } from "@src/validation/EventListValidation";
import { RootState } from "@src/store/store";
import { fetchEventCities } from "@src/store/events/eventCitiesSlice";
import { fetchVenues } from "@src/store/events/eventVenuesSlice";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { FilterField } from "@src/components/filtersPanel/FilterField";
import { FilterSection } from "@src/components/filtersPanel/FilterSection";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";

interface IAllFilters {
	control: Control;
	setValue: UseFormSetValue<EventListHeaderFormProps>;
	reset: UseFormReset<EventListHeaderFormProps>;
	savedFilters: Array<EventListHeaderFormProps>;
	selectedFilterIndex?: number | null;
	setSelectedFilterIndex?: (idx: number | null) => void;
	onSaveFilters: () => void;
}

export const EventFilters: React.FC<IAllFilters> = (props) => {
	const { control, savedFilters, setValue, reset, selectedFilterIndex, setSelectedFilterIndex, onSaveFilters } = props;

	const dispatch = useAppDispatch();
	const { data: categories, loading: categoriesLoading } = useAppSelector((state: RootState) => state.events.categories);
	const { data: artists, loading: artistsLoading } = useAppSelector((state: RootState) => state.events.artists);
	const { data: states, loading: statesLoading } = useAppSelector((state: RootState) => state.events.states);
	const { data: cities, loading: citiesLoading } = useAppSelector((state: RootState) => state.events.cities);
	const { data: venues, loading: venuesLoading } = useAppSelector((state: RootState) => state.events.venues);
	const { data: eventStatuses, loading: eventStatusesLoading } = useAppSelector((state: RootState) => state.events.statuses);

	const selectedState = useWatch({ control, name: "state" });
	const selectedCity = useWatch({ control, name: "city" });
	const selectedPresaleStartDate = useWatch({ control, name: "presaleStartDateTime" });
	const selectedPresaleEndDate = useWatch({ control, name: "presaleEndDateTime" });
	const selectedEventStartDate = useWatch({ control, name: "eventStartDate" });
	const selectedEventEndDate = useWatch({ control, name: "eventEndDate" });

	// Track previous state and city to avoid clearing on initial mount or when restoring same value
	const prevStateRef = useRef<string | undefined>(undefined);
	const prevCityRef = useRef<string | undefined>(undefined);
	const [isApplyingSavedFilter, setIsApplyingSavedFilter] = useState(false);

	useEffect(() => {
		if (selectedState) {
			if (isApplyingSavedFilter) {
				// If applying saved filter, do not clear or fetch
				prevStateRef.current = selectedState;
				return;
			}
			if (prevStateRef.current === undefined) {
				// initial mount: populate options; do not clear selections
				dispatch(fetchEventCities({ countryCode: "US", stateCode: selectedState }));
			} else if (prevStateRef.current !== selectedState) {
				dispatch(fetchEventCities({ countryCode: "US", stateCode: selectedState }));
				setValue("city", "");
				setValue("venueName", "");
			}
		}
		prevStateRef.current = selectedState;
	}, [dispatch, selectedState, setValue]);

	useEffect(() => {
		if (selectedCity) {
			if (isApplyingSavedFilter) {
				// If applying saved filter, do not clear or fetch
				prevCityRef.current = selectedCity;
				return;
			}
			if (prevCityRef.current === undefined) {
				dispatch(fetchVenues({ cityName: selectedCity }));
			} else if (prevCityRef.current !== selectedCity) {
				dispatch(fetchVenues({ cityName: selectedCity }));
				setValue("venueName", "");
			}
		}
		prevCityRef.current = selectedCity;
	}, [dispatch, selectedCity, setValue]);

	useEffect(() => {
		if (!selectedPresaleStartDate && !selectedPresaleEndDate) {
			setValue("presaleStartTime", null);
			setValue("presaleEndTime", null);
		}
	}, [selectedPresaleStartDate, selectedPresaleEndDate, setValue]);

	useEffect(() => {
		if (isApplyingSavedFilter) {
			setIsApplyingSavedFilter(false);
		}
	}, [selectedState, selectedCity]);

	const categoryOptions = (categories ?? []).map((cat: string) => ({
		label: cat,
		value: cat,
	}));
	const artistOptions = (artists ?? []).map((artist: string) => ({
		label: artist,
		value: artist,
	}));
	const eventStatusOptions = (eventStatuses ?? []).map((status: string) => ({
		label: status,
		value: status,
	}));
	const stateOptions = (states ?? []).map((state: string) => ({
		label: state,
		value: state,
	}));
	const cityOptions = selectedState
		? (cities ?? []).map((city: string) => ({
				label: city,
				value: city,
			}))
		: [];
	const venueOptions = selectedCity
		? (venues ?? []).map((venue: string) => ({
				label: venue,
				value: venue,
			}))
		: [];

	const savedFilterOptions = (savedFilters ?? []).map((item: EventListHeaderFormProps, index: number) => ({
		id: index + 2,
		label: `Saved Filters ${index + 1}`,
		value: item,
		onClick: (value: EventListHeaderFormProps) => {
			reset(value);
			setIsApplyingSavedFilter(true);
			setSelectedFilterIndex && setSelectedFilterIndex(index); // Track which filter is being edited
		},
	}));
	const filterOptionsWithReset = [
		{
			id: 0,
			label: "Select",
			onClick: reset,
		},
		{
			id: 1,
			label: "Save current filters",
			onClick: onSaveFilters,
		},
		...savedFilterOptions,
	];

	return (
		<>
			<FilterSection title="Search">
				<FilterField label="Smart search">
					<Input control={control} name="smartSearch" placeholder="Event Name, Venue, etc." />
				</FilterField>
			</FilterSection>

			<FilterSection title="Event">
				<FilterField label="Event category">
					<SelectYup
						name="eventCategory"
						placeholder="Event Category"
						control={control}
						options={categoryOptions}
						isLoading={categoriesLoading}
					/>
				</FilterField>

				<FilterField label="Event name">
					<Input control={control} name="eventName" placeholder="Event Name" />
				</FilterField>

				<FilterField label="Event headliner">
					<SelectYup
						name="artistName"
						placeholder="Event Headliner"
						control={control}
						options={artistOptions}
						isLoading={artistsLoading}
					/>
				</FilterField>

				<div className={styles.row}>
					<FilterField label="Start date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="eventStartDate"
							placeholder="Start Date"
							popperPlacement="bottom-start"
							minDate={new Date()}
							maxDate={selectedEventEndDate ? new Date(selectedEventEndDate) : undefined}
						/>
					</FilterField>
					<FilterField label="End date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="eventEndDate"
							placeholder="End Date"
							popperPlacement="bottom-end"
							minDate={selectedEventStartDate ? new Date(selectedEventStartDate) : new Date()}
						/>
					</FilterField>
				</div>

				<FilterField label="Event status">
					<SelectYup
						name="eventStatus"
						placeholder="Event Status"
						control={control}
						options={eventStatusOptions}
						isLoading={eventStatusesLoading}
					/>
				</FilterField>

				<FilterField label="State">
					<SelectYup name="state" placeholder="State" control={control} options={stateOptions} isLoading={statesLoading} />
				</FilterField>

				<FilterField label="City">
					<SelectYup name="city" placeholder="City" control={control} options={cityOptions} isLoading={citiesLoading} />
				</FilterField>

				<FilterField label="Venue">
					<SelectYup
						name="venueName"
						placeholder="Venue"
						control={control}
						options={venueOptions}
						isLoading={venuesLoading}
					/>
				</FilterField>
			</FilterSection>

			<FilterSection title="Presale & Onsale">
				<div className={styles.row}>
					<FilterField label="Start date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="presaleStartDateTime"
							placeholder="Start Date"
							popperPlacement="bottom-start"
							minDate={new Date()}
							maxDate={selectedPresaleEndDate ? new Date(selectedPresaleEndDate) : undefined}
						/>
					</FilterField>
					<FilterField label="End date">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="presaleEndDateTime"
							placeholder="End Date"
							popperPlacement="bottom-end"
							minDate={selectedPresaleStartDate ? new Date(selectedPresaleStartDate) : new Date()}
						/>
					</FilterField>
				</div>

				<div className={styles.row}>
					<FilterField label="Start time">
						<TimeInput
							control={control}
							name="presaleStartTime"
							placeholder="Start Time"
							disabled={!selectedPresaleStartDate && !selectedPresaleEndDate}
						/>
					</FilterField>
					<FilterField label="End time">
						<TimeInput
							control={control}
							name="presaleEndTime"
							placeholder="End Time"
							disabled={!selectedPresaleStartDate && !selectedPresaleEndDate}
						/>
					</FilterField>
				</div>
			</FilterSection>

			<FilterSection title="Ranges">
				<FilterField label="Capacity range">
					<RangeSlider name="capacityRange" control={control} min={0} max={60000} minDistance={500} disabled={true} />
				</FilterField>

				<FilterField label="Demand score range">
					<RangeSlider name="aiRange" control={control} />
				</FilterField>
			</FilterSection>

			<div className={styles.savedSearch}>
				<div className={styles.fieldLabel}>Saved filters</div>
				<Dropdown
					dynamicText={false}
					isDisabled={false}
					containerWidth="100%"
					items={filterOptionsWithReset as Array<IItems>}
					buttonText={selectedFilterIndex == null ? "Select" : savedFilterOptions[selectedFilterIndex]?.label}
				/>
			</div>
		</>
	);
};
